/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState } from 'react';
import { Modal, Tabs } from 'antd';
import type { ModalProps } from 'antd/lib/modal';
import { useIntl } from 'react-intl';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import IconButton from '~components/_common/IconButton/IconButton';
import { useProjectId } from '~src/hooks/useProjectId';
import { useTenderId } from '~src/hooks/useTenderId';
import { useRegistrationCoreTendersDetails } from '~src/hooks/api-hooks/useRegistrationCoreTendersDetails';
import { InviteTab } from './Tabs/InviteTab';
import { ImportTab } from './Tabs/ImportTab';

const { TabPane } = Tabs;

type InviteModalMode = 'invite' | 'edit-resend';

interface InviteModalProps extends ModalProps {
  mode?: InviteModalMode;
  initialShares?: any[] | any;
}

export const InviteModal: React.FC<InviteModalProps> = ({
  onCancel,
  mode = 'invite',
  initialShares,
  ...modalProps
}) => {
  const intl = useIntl();
  const t = (id: string, def: string) =>
    intl.formatMessage({ id, defaultMessage: def });
  const styles = useStyles(styleFn);
  const projectId = useProjectId();
  const tenderId = useTenderId();
  const isEditResendMode = mode === 'edit-resend';

  const tenderDetails = useRegistrationCoreTendersDetails({
    tenderId: tenderId ? String(tenderId) : '',
  });
  const tenderIsCompleted = tenderDetails?.state === 6;

  const [activeTab, setActiveTab] = useState<string>('invite');
  const [canSend, setCanSend] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const isModalVisible =
    (modalProps as any).visible ?? (modalProps as any).open ?? false;

  const handleSend = () => {
    // Call the send handler from the InviteTab
    if (activeTab === 'invite' && (window as any).__inviteTabSendHandler) {
      (window as any).__inviteTabSendHandler();
    }
  };

  const handleCancel = (e: any) => {
    setActiveTab('invite');
    onCancel && onCancel(e);
  };

  const modalTitle = isEditResendMode
    ? t('invite-edit-resend-invite', 'Edit & resend invitation')
    : t('invite-share', 'Share');

  const okText = isEditResendMode
    ? t('invite-invite-selected', 'Invite selected')
    : t('invite-send', 'Send');

  return (
    <Modal
      title={
        <div css={styles.titleContainer}>
          <IconButton iconStyle="far" icon="paper-plane" backgroundOff />
          {modalTitle}
        </div>
      }
      cancelText={t('invite-cancel', 'Cancel')}
      okText={okText}
      width={1100}
      centered
      maskClosable={false}
      onCancel={handleCancel}
      onOk={handleSend}
      okButtonProps={{
        disabled: activeTab !== 'invite' || !canSend || isSending,
        loading: isSending,
      }}
      footer={activeTab === 'import' ? null : undefined}
      {...modalProps}>
      <Tabs activeKey={activeTab} onChange={setActiveTab} css={styles.tabs}>
        <TabPane tab={t('invite-tab-invite', 'Invite')} key="invite">
          <InviteTab
            mode={mode}
            initialShares={initialShares}
            projectId={projectId ?? 0}
            tenderId={tenderId ?? 0}
            tenderCompleted={tenderIsCompleted}
            onSendClick={handleSend}
            onCanSendChange={setCanSend}
            onSendingChange={setIsSending}
            isModalVisible={isModalVisible}
          />
        </TabPane>
        {!isEditResendMode && (
          <TabPane tab={t('invite-tab-import', 'Import')} key="import">
            <ImportTab
              projectId={projectId ?? 0}
              tenderId={tenderId ?? 0}
              tenderCompleted={tenderIsCompleted}
            />
          </TabPane>
        )}
      </Tabs>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = () => ({
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 18,
    fontWeight: 700,
  },

  tabs: {
    '& .ant-tabs-nav': {
      marginBottom: 16,
    },
    '& .ant-tabs-content': {
      paddingTop: 12,
    },
  },
});

export default InviteModal;
